import { useTerminalTheme } from "@/lib/terminal/use-terminal-theme"
import { cn } from "@/lib/utils"

/* The reference's sliding-slab theme toggle: two 28px square seats
 * (sun = light, moon = dark) in a recessed well; a single slab slides
 * between them — the slab IS the selector. Glyphs are feather-derived:
 * a balanced 8-ray line sun and a clean line crescent moon. */

const SUN_RAYS =
  "M12 1.6v2.3M12 20.1v2.3M4.22 4.22l1.63 1.63M18.15 18.15l1.63 1.63" +
  "M1.6 12h2.3M20.1 12h2.3M4.22 19.78l1.63-1.63M18.15 5.85l1.63-1.63"
const MOON_D = "M20.6 13.2A8.4 8.4 0 1 1 10.8 3.4 6.6 6.6 0 0 0 20.6 13.2z"

const GLYPH_SVG_PROPS = {
  className: "block",
  width: 16,
  height: 16,
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.7,
  strokeLinecap: "round",
  strokeLinejoin: "round",
  // eslint-disable-next-line english-naming/boolean-prefix -- The SVG/ARIA attribute contract owns this key.
  "aria-hidden": true,
} as const

const seatClassName = (isActive: boolean) =>
  cn(
    "rounded-sm relative z-10 flex size-7 cursor-pointer items-center justify-center bg-transparent transition-[background-color,color] duration-(--duration-fast) ease-(--ease-terminal) focus-visible:outline-none",
    isActive
      ? "text-invert-text"
      : "text-muted-foreground hover:bg-accent hover:text-foreground focus-visible:bg-accent focus-visible:text-foreground"
  )

export const ThemeToggle = ({ className }: { className?: string }) => {
  const { theme, setTerminalTheme } = useTerminalTheme()
  return (
    <div
      className={cn(
        "bg-card relative inline-flex min-h-8 flex-none gap-0.5 rounded-sm p-0.5",
        className
      )}
      role="group"
      aria-label="Color theme"
    >
      <span
        className={cn(
          "bg-invert-bg absolute top-0.5 z-0 h-7 w-7 rounded-sm transition-[left,background-color] duration-(--duration-base) ease-(--ease-terminal)",
          theme === "light" ? "left-0.5" : "left-8"
        )}
        aria-hidden="true"
      />
      <button
        type="button"
        className={seatClassName(theme === "light")}
        aria-label="Light mode"
        aria-pressed={theme === "light"}
        onClick={() => setTerminalTheme("light")}
      >
        <svg {...GLYPH_SVG_PROPS}>
          <circle cx="12" cy="12" r="4" />
          <path d={SUN_RAYS} />
        </svg>
      </button>
      <button
        type="button"
        className={seatClassName(theme === "dark")}
        aria-label="Dark mode"
        aria-pressed={theme === "dark"}
        onClick={() => setTerminalTheme("dark")}
      >
        <svg {...GLYPH_SVG_PROPS}>
          <path d={MOON_D} />
        </svg>
      </button>
    </div>
  )
}
